import React from "react";
import {
  Box,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Divider,
} from "@mui/material";
import { VoucherFiltersProps } from "../types";
import { useBranch } from "../../../components/BranchContext";
import {
  VOUCHER_TYPES,
  PAYMENT_METHODS,
  VOUCHER_STATUS,
} from "../utils/voucherUtils";

export const VoucherFilters: React.FC<VoucherFiltersProps> = ({
  filters,
  onFiltersChange,
}) => {
  const { selectedBranch } = useBranch();

  const handleFieldChange = (field: keyof typeof filters, value: string) => {
    onFiltersChange({ [field]: value });
  };

  // TODO: Replace with real branch data from branch management API
  const mockBranches = [
    { branch_id: "branch1", name: "Main Branch" },
    { branch_id: "branch2", name: "Secondary Branch" },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Date Range */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Date Range
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            size="small"
            label="From Date"
            type="date"
            value={filters.dateFrom || ""}
            onChange={(e) => handleFieldChange("dateFrom", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            size="small"
            label="To Date"
            type="date"
            value={filters.dateTo || ""}
            onChange={(e) => handleFieldChange("dateTo", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Voucher Details */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Voucher Details
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            select
            label="Voucher Type"
            value={filters.voucherType || ""}
            onChange={(e) => handleFieldChange("voucherType", e.target.value)}
          >
            <MenuItem value="">All Types</MenuItem>
            {VOUCHER_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            select
            label="Status"
            value={filters.status || ""}
            onChange={(e) => handleFieldChange("status", e.target.value)}
          >
            <MenuItem value="">All Status</MenuItem>
            {VOUCHER_STATUS.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            select
            label="Payment Method"
            value={filters.paymentMethod || ""}
            onChange={(e) => handleFieldChange("paymentMethod", e.target.value)}
          >
            <MenuItem value="">All Methods</MenuItem>
            {PAYMENT_METHODS.map((method) => (
              <MenuItem key={method.value} value={method.value}>
                {method.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Branch & User */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Location & User
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            select
            label="Branch"
            value={filters.selectedBranch || ""}
            onChange={(e) =>
              handleFieldChange("selectedBranch", e.target.value)
            }
          >
            <MenuItem value="">All Branches</MenuItem>
            <MenuItem value="organization">Organization</MenuItem>
            {/* TODO: Replace with real branch data from branch management API */}
            {mockBranches.map((branch) => (
              <MenuItem key={branch.branch_id} value={branch.branch_id}>
                {branch.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Posted By"
            placeholder="Enter user name..."
            value={filters.postedBy || ""}
            onChange={(e) => handleFieldChange("postedBy", e.target.value)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Search Fields */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Search Fields
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Voucher ID"
            placeholder="e.g., PV-2024-0001"
            value={filters.voucherId || ""}
            onChange={(e) => handleFieldChange("voucherId", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Beneficiary"
            placeholder="Enter beneficiary name..."
            value={filters.beneficiary || ""}
            onChange={(e) => handleFieldChange("beneficiary", e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Linked Entity"
            placeholder="e.g., INV-0040, ORD-2024-0156"
            value={filters.linkedEntity || ""}
            onChange={(e) => handleFieldChange("linkedEntity", e.target.value)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Amount Range */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Amount Range
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            size="small"
            label="Min Amount"
            type="number"
            placeholder="0.000"
            value={filters.amountFrom || ""}
            onChange={(e) => handleFieldChange("amountFrom", e.target.value)}
            inputProps={{ step: "0.001", min: "0" }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            size="small"
            label="Max Amount"
            type="number"
            placeholder="999999.999"
            value={filters.amountTo || ""}
            onChange={(e) => handleFieldChange("amountTo", e.target.value)}
            inputProps={{ step: "0.001", min: "0" }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
